import * as XLSX from 'xlsx';

export interface XlsxRow {
  tipoSegmentacao: string;
  idRegra: string;
  ean: string;
  bu: string;
  budget: number;
  pctIndustria: number;
  pctDistribuidor: number;
  pctMarketplace: number;
  pctExecutivo: number;
  rowNumber: number;
}

export interface ParsedPromocao {
  idRegra: string;
  tipoSegmentacao: 'IDRegra' | 'BU' | 'EAN';
  rows: XlsxRow[];
  totalBudget: number;
}

export interface ValidationError {
  row?: number;
  idRegra?: string;
  field: string;
  message: string;
  severity: 'error' | 'warning';
}

export interface ImportResult {
  promocoes: ParsedPromocao[];
  errors: ValidationError[];
  totalRows: number;
  validRows: number;
}

const normalizeHeader = (header: string): string => {
  return header
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[%\s]+/g, '');
};

const HEADER_MAP: Record<string, string> = {
  'tipodesegmentacao': 'tipoSegmentacao',
  'tiposegmentacao': 'tipoSegmentacao',
  'idregra': 'idRegra',
  'ean': 'ean',
  'bu': 'bu',
  'budget': 'budget',
  'industria': 'pctIndustria',
  'distribuidor': 'pctDistribuidor',
  'marketplace': 'pctMarketplace',
  'executivo': 'pctExecutivo',
};

function mapHeaders(rawHeaders: string[]): Record<number, string> {
  const mapping: Record<number, string> = {};
  rawHeaders.forEach((header, index) => {
    const normalized = normalizeHeader(header);
    if (HEADER_MAP[normalized]) {
      mapping[index] = HEADER_MAP[normalized];
    }
  });
  return mapping;
}

function parseNumber(value: any): number {
  if (value === null || value === undefined || value === '') return 0;
  const num = parseFloat(String(value).replace(',', '.'));
  return isNaN(num) ? 0 : num;
}

function isRowEmpty(row: any[]): boolean {
  return row.every(cell => cell === null || cell === undefined || String(cell).trim() === '');
}

export function parseXlsxFile(file: File): Promise<ImportResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const rawData: any[][] = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

        if (rawData.length < 2) {
          resolve({
            promocoes: [],
            errors: [{ field: 'arquivo', message: 'Arquivo vazio ou sem dados suficientes', severity: 'error' }],
            totalRows: 0,
            validRows: 0,
          });
          return;
        }

        const headerMapping = mapHeaders(rawData[0].map(String));
        const requiredFields = ['tipoSegmentacao', 'idRegra'];
        const missingFields = requiredFields.filter(f => !Object.values(headerMapping).includes(f));

        if (missingFields.length > 0) {
          resolve({
            promocoes: [],
            errors: [{
              field: 'cabecalho',
              message: `Colunas obrigatórias não encontradas: ${missingFields.join(', ')}`,
              severity: 'error',
            }],
            totalRows: 0,
            validRows: 0,
          });
          return;
        }

        const rows: XlsxRow[] = [];
        const errors: ValidationError[] = [];

        for (let i = 1; i < rawData.length; i++) {
          const rawRow = rawData[i];
          if (!rawRow || isRowEmpty(rawRow)) continue;

          const row: any = { rowNumber: i + 1 };
          Object.entries(headerMapping).forEach(([colIdx, fieldName]) => {
            const value = rawRow[parseInt(colIdx)];
            if (['budget', 'pctIndustria', 'pctDistribuidor', 'pctMarketplace', 'pctExecutivo'].includes(fieldName)) {
              row[fieldName] = parseNumber(value);
            } else {
              row[fieldName] = value !== null && value !== undefined ? String(value).trim() : '';
            }
          });

          if (!row.tipoSegmentacao && !row.idRegra) continue;

          row.tipoSegmentacao = row.tipoSegmentacao || '';
          row.idRegra = row.idRegra || '';
          row.ean = row.ean || '';
          row.bu = row.bu || '';
          row.budget = row.budget || 0;
          row.pctIndustria = row.pctIndustria || 0;
          row.pctDistribuidor = row.pctDistribuidor || 0;
          row.pctMarketplace = row.pctMarketplace || 0;
          row.pctExecutivo = row.pctExecutivo || 0;

          rows.push(row as XlsxRow);
        }

        rows.forEach((row) => {
          if (!row.tipoSegmentacao) {
            errors.push({
              row: row.rowNumber,
              idRegra: row.idRegra,
              field: 'Tipo de Segmentação',
              message: `Linha ${row.rowNumber}: Tipo de Segmentação não informado`,
              severity: 'error',
            });
          }

          if (!row.idRegra) {
            errors.push({
              row: row.rowNumber,
              field: 'IDRegra',
              message: `Linha ${row.rowNumber}: IDRegra não informado`,
              severity: 'error',
            });
          }

          const tipo = row.tipoSegmentacao.toUpperCase();
          if (tipo === 'BU' && row.ean) {
            errors.push({
              row: row.rowNumber,
              idRegra: row.idRegra,
              field: 'BU/EAN',
              message: `Linha ${row.rowNumber}: Quando BU está preenchido, EAN não pode estar preenchido`,
              severity: 'error',
            });
          }
          if (tipo === 'EAN' && row.bu) {
            errors.push({
              row: row.rowNumber,
              idRegra: row.idRegra,
              field: 'EAN/BU',
              message: `Linha ${row.rowNumber}: Quando EAN está preenchido, BU não pode estar preenchido`,
              severity: 'error',
            });
          }
          if (tipo === 'BU' && !row.bu) {
            errors.push({
              row: row.rowNumber,
              idRegra: row.idRegra,
              field: 'BU',
              message: `Linha ${row.rowNumber}: Tipo BU requer campo BU preenchido`,
              severity: 'error',
            });
          }
          if (tipo === 'EAN' && !row.ean) {
            errors.push({
              row: row.rowNumber,
              idRegra: row.idRegra,
              field: 'EAN',
              message: `Linha ${row.rowNumber}: Tipo EAN requer campo EAN preenchido`,
              severity: 'error',
            });
          }

          const total = row.pctIndustria + row.pctDistribuidor + row.pctMarketplace + row.pctExecutivo;
          const allEmpty = row.pctIndustria === 0 && row.pctDistribuidor === 0 && row.pctMarketplace === 0 && row.pctExecutivo === 0;
          if (!allEmpty && Math.abs(total - 100) > 0.01) {
            errors.push({
              row: row.rowNumber,
              idRegra: row.idRegra,
              field: 'Porcentagens',
              message: `Linha ${row.rowNumber}: A soma das porcentagens é ${total}%, deve ser 100%`,
              severity: 'error',
            });
          }
        });

        const grouped: Record<string, XlsxRow[]> = {};
        rows.forEach(row => {
          if (!row.idRegra) return;
          if (!grouped[row.idRegra]) grouped[row.idRegra] = [];
          grouped[row.idRegra].push(row);
        });

        Object.entries(grouped).forEach(([idRegra, groupRows]) => {
          const tipos = new Set(groupRows.map(r => r.tipoSegmentacao.toUpperCase()));
          if (tipos.size > 1) {
            errors.push({
              idRegra,
              field: 'Tipo de Segmentação',
              message: `IDRegra ${idRegra}: Tipos de segmentação mistos (${Array.from(tipos).join(', ')})`,
              severity: 'error',
            });
          }

          const tipo = groupRows[0].tipoSegmentacao.toUpperCase();
          if (tipo === 'IDREGRA' && groupRows.length > 1) {
            errors.push({
              idRegra,
              field: 'IDRegra',
              message: `IDRegra ${idRegra}: Segmentação por IDRegra só pode ter uma linha, encontradas ${groupRows.length}`,
              severity: 'error',
            });
          }
        });

        const promocoes: ParsedPromocao[] = Object.entries(grouped).map(([idRegra, groupRows]) => {
          const tipoRaw = groupRows[0].tipoSegmentacao.toUpperCase();
          let tipoSegmentacao: 'IDRegra' | 'BU' | 'EAN' = 'IDRegra';
          if (tipoRaw === 'BU') tipoSegmentacao = 'BU';
          else if (tipoRaw === 'EAN') tipoSegmentacao = 'EAN';

          const totalBudget = tipoSegmentacao === 'EAN'
            ? (groupRows.find(r => r.budget > 0)?.budget || 0)
            : groupRows.reduce((sum, r) => sum + r.budget, 0);

          return { idRegra, tipoSegmentacao, rows: groupRows, totalBudget };
        });

        resolve({
          promocoes,
          errors,
          totalRows: rows.length,
          validRows: rows.length - errors.filter(e => e.severity === 'error').length,
        });
      } catch (err) {
        reject(new Error('Erro ao processar arquivo: ' + (err as Error).message));
      }
    };

    reader.onerror = () => reject(new Error('Erro ao ler o arquivo'));
    reader.readAsArrayBuffer(file);
  });
}

export function convertToApiPayloads(promocoes: ParsedPromocao[]): any[] {
  return promocoes.map(promo => {
    const configuracoes = (() => {
      switch (promo.tipoSegmentacao) {
        case 'IDRegra': {
          const row = promo.rows[0];
          return [{
            id: promo.idRegra,
            nome: `ID Regra: ${promo.idRegra}`,
            budget: row.budget,
            multipatrocinadores: {
              coluna1: { tipo: 'Industria', porcentagem: row.pctIndustria },
              coluna2: { tipo: 'Distribuidor', porcentagem: row.pctDistribuidor },
              coluna3: { tipo: 'Marketplace', porcentagem: row.pctMarketplace },
              coluna4: { tipo: 'Executivo', porcentagem: row.pctExecutivo },
            },
          }];
        }
        case 'BU':
          return promo.rows.map(row => ({
            id: `bu_${row.bu}`,
            nome: row.bu,
            budget: row.budget,
            multipatrocinadores: {
              coluna1: { tipo: 'Industria', porcentagem: row.pctIndustria },
              coluna2: { tipo: 'Distribuidor', porcentagem: row.pctDistribuidor },
              coluna3: { tipo: 'Marketplace', porcentagem: row.pctMarketplace },
              coluna4: { tipo: 'Executivo', porcentagem: row.pctExecutivo },
            },
          }));
        case 'EAN':
          return promo.rows.map(row => ({
            id: `ean_${row.ean}`,
            nome: row.ean,
            budget: 0,
            multipatrocinadores: {
              coluna1: { tipo: 'Industria', porcentagem: row.pctIndustria },
              coluna2: { tipo: 'Distribuidor', porcentagem: row.pctDistribuidor },
              coluna3: { tipo: 'Marketplace', porcentagem: row.pctMarketplace },
              coluna4: { tipo: 'Executivo', porcentagem: row.pctExecutivo },
            },
          }));
        default:
          return [];
      }
    })();

    return {
      idRegraPromocao: promo.idRegra,
      nomePromocao: '',
      budgetPlataforma: 0,
      novoBudget: promo.totalBudget,
      confirmarBudget: true,
      tipoSegmentacao: promo.tipoSegmentacao,
      segmentacao: {
        tipo: promo.tipoSegmentacao,
        configuracoes,
      },
    };
  });
}
